import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  pipelineApi,
  type TrackerBoard,
  type TrackerBoardCard,
  type TrackerMappingReport,
} from '../lib/api';
import { AuroraPage, PageHeader, Card, Button, Badge, Icon, Tabs } from '../components/ui';

type ListKey = 'all' | 'bg_pending' | 'waiting_on_client';

function daysLabel(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${n} day${n === 1 ? '' : 's'} in stage`;
}

function PersonRow({ card, onOpen }: { card: TrackerBoardCard; onOpen: (id: string) => void }) {
  const name = `${card.first_name ?? ''} ${card.last_name ?? ''}`.trim() || 'Unnamed';
  return (
    <button
      type="button"
      onClick={() => onOpen(card.id)}
      style={{
        display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center',
        width: '100%', textAlign: 'left', background: 'var(--sf2)', border: '1px solid var(--bd)',
        borderRadius: 'var(--r-sm)', padding: '10px 12px', cursor: 'pointer',
      }}
    >
      <span>
        <span style={{ display: 'block', fontWeight: 600, fontSize: 14, color: 'var(--t1)' }}>{name}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>
          {[card.role, card.recruiter_name, card.email].filter(Boolean).join(' · ') || 'No contact on file'}
          {card.view_mapped && card.stored_stage ? ` · stored as ${card.stored_stage}` : ''}
        </span>
      </span>
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        {card.requires_jail_application && <Badge tone="warn">Jail app</Badge>}
        {card.is_stale && <Badge tone="danger">Stale</Badge>}
        <span style={{ fontSize: 12, color: 'var(--t2)', whiteSpace: 'nowrap' }}>{daysLabel(card.days_in_stage)}</span>
      </span>
    </button>
  );
}

export default function CandidateTracker() {
  const nav = useNavigate();
  const [board, setBoard] = useState<TrackerBoard | null>(null);
  const [list, setList] = useState<ListKey>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<TrackerMappingReport | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pipelineApi.tracker();
      setBoard(res.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      setError(ax?.response?.data?.error ?? ax?.message ?? 'Failed to load the tracker');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const loadReport = async () => {
    setReportLoading(true);
    setReportError(null);
    try {
      const res = await pipelineApi.trackerMappingReport();
      setReport(res.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      setReportError(ax?.response?.data?.error ?? ax?.message ?? 'Failed to load the mapping preview');
    } finally {
      setReportLoading(false);
    }
  };

  const fns = board?.mode === 'fns';
  const sections = !board ? [] : list === 'bg_pending'
    ? [{ key: 'bg', label: board.lists.bg_pending.label, items: board.lists.bg_pending.items }]
    : list === 'waiting_on_client'
      ? [{ key: 'wait', label: board.lists.waiting_on_client.label, items: board.lists.waiting_on_client.items }]
      : [
          ...board.stages.map((s) => ({ key: s.key, label: `${s.label} (${s.count})`, items: s.items })),
          ...(board.other.count > 0 ? [{ key: 'other', label: `${board.other.label} (${board.other.count})`, items: board.other.items }] : []),
        ];

  return (
    <AuroraPage>
      <PageHeader
        icon={<Icon name="columns" size={22} />}
        title="Candidate tracker"
        subtitle="Where each candidate stands. This view does not change anyone's stored stage and does not send email or text."
        actions={<Button variant="secondary" size="sm" onClick={() => void load()}>Refresh</Button>}
      />

      {loading && <div style={{ padding: 24, color: 'var(--t3)' }}>Loading…</div>}
      {error && <div style={{ padding: 16, color: 'var(--danger-fg)' }}>{error}</div>}

      {board && (
        <>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--t2)' }}>
            {fns
              ? `${board.total} active candidate${board.total === 1 ? '' : 's'}. People still stored on an older stage appear in the closest new column. Their stored stage is unchanged.`
              : 'This tenant uses the standard pipeline. The new columns are not applied.'}
          </p>

          {fns && (
            <Tabs
              aria-label="Candidate tracker lists"
              value={list}
              onChange={(k) => setList(k as ListKey)}
              tabs={[
                { key: 'all', label: 'All stages' },
                { key: 'bg_pending', label: `BG pending (${board.lists.bg_pending.count})` },
                { key: 'waiting_on_client', label: `Submitted, waiting on client (${board.lists.waiting_on_client.count})` },
              ]}
            />
          )}

          <div style={{ display: 'grid', gap: 16 }}>
            {sections.map((section) => (
              <Card key={section.key} title={section.label}>
                {section.items.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--t3)' }}>No one here.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {section.items.map((card) => (
                      <PersonRow key={card.id} card={card} onOpen={(id) => nav(`/candidates/${id}`)} />
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>

          {fns && (
            <Card title="Stage mapping preview" style={{ marginTop: 16 }}>
              <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--t2)' }}>
                A dry run of how stored stages would appear on this board. It does not move anyone.
              </p>
              {!report && (
                <Button size="sm" variant="secondary" onClick={() => void loadReport()} disabled={reportLoading}>
                  {reportLoading ? 'Loading…' : 'Show mapping preview'}
                </Button>
              )}
              {reportError && <div style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{reportError}</div>}
              {report && (
                <div style={{ fontSize: 13, color: 'var(--t2)' }}>
                  <div style={{ marginBottom: 8 }}>
                    Writes: {report.writes}. Mapped in the view: {report.mapped}. Already on a new stage: {report.native}. Left on their stored stage: {report.unmapped}.
                  </div>
                  <div style={{ display: 'grid', gap: 4 }}>
                    {Object.entries(report.by_stored_stage).map(([stage, info]) => (
                      <div key={stage}>
                        {stage || '(blank)'} → {info.view_stage ?? 'unchanged'} ({info.count})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </AuroraPage>
  );
}
